import { useContext, useEffect, useState } from 'react';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { IconPlus } from '@tabler/icons-react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@common/utils';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';
import { LangContext } from '@/context/LangProvider';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import RequiredLabel from '@/components/labels/RequiredLabel';
import QuestionBox from './QuestionBox';
import useCreateProjectStore from '@/store/CreateProjectStore';
import { DiscussionGuideProps, QuestionBoxProps } from '@/types';
import DeleteQuestionModal from '@/components/Modals/DeleteQuestionModal';

const QUESTIONS_LIMIT = 40;
const initialQuestion = {
  question: '',
  follow_up_question: '0',
  media: undefined,
  answer_quantification: [
    {
      quantify_answer: false,
      value: '',
    },
  ],
};

function DiscussionGuide({
  singleProjectData,
  isProjectPublished,
  isProjectCompleted,
  register,
  control,
  setValue,
  getValues,
  clearErrors,
  setFileData,
  fileData,
  currentQuestions,
  errors,
  onSubmit,
}: DiscussionGuideProps) {
  const { lang } = useContext(LangContext);
  const { setIsFormChanged, setReorderChanged, uploadingQuestionId } = useCreateProjectStore();
  const [questions, setQuestions] = useState(singleProjectData?.interview_questions || []);
  const { isUserViewer } = useWorkspaceRoles();
  const [currentFormValues] = useState({});
  const [isActiveDrag, setIsActiveDrag] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<string | null>(null);
  const [isDeletingQuestion, setIsDeletingQuestion] = useState(false);
  const [openQuestionId, setOpenQuestionId] = useState<string | null>(null);
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active?.id !== over?.id) {
      setQuestions((items) => {
        const activeIndex = items.findIndex((q) => q?.id === active?.id);
        const overIndex = items.findIndex((q) => q?.id === over?.id);

        const reorderedItems = arrayMove(items, activeIndex, overIndex);

        // Update the form values based on the reordered items
        const reorderedFormValues = reorderedItems.map((item) => {
          const matchingValue = currentFormValues?.interview_questions?.find(
            (q) => q?.id === item.id,
          );

          return matchingValue || item;
        });

        // Update the form values using setValue
        setValue('interview_questions', reorderedFormValues, { shouldDirty: true });
        setReorderChanged(true);

        return reorderedItems;
      });
    }

    setIsActiveDrag(false);
  };

  const addQuestion = () => {
    const newQuestion: QuestionBoxProps = { ...initialQuestion, id: uuidv4() };

    // Update the state
    setQuestions((prevQuestions) => [...(prevQuestions || []), newQuestion]);

    // Retrieve the current form values
    const currentFormValuesAdd = getValues();

    // Update the form values using setValue
    setValue(
      'interview_questions',
      [...(currentFormValuesAdd.interview_questions || []), newQuestion],
      { shouldDirty: true },
    );
    setOpenQuestionId(newQuestion.id);
  };

  const handleDeleteQuestion = (idToDelete: string) => {
    // Check if the question being deleted exists in the database
    const questionExistsInDB = singleProjectData?.interview_questions?.some(
      ({ id }) => id === idToDelete,
    );

    const currentFormValuesDelete = getValues();

    const updatedInterviewQuestions =
      currentFormValuesDelete.interview_questions?.filter(
        (q: { id: string }) => q.id !== idToDelete,
      ) || [];

    // If the question doesn't exist in DB, just update local state
    if (!questionExistsInDB) {
      // Find the index of the question being deleted to clear its error
      const questionIndex = currentFormValuesDelete.interview_questions?.findIndex(
        (q: { id: string }) => q.id === idToDelete,
      );

      if (questionIndex !== -1) {
        clearErrors(`interview_questions.${questionIndex}.question`);
      }

      setQuestions((prevQuestions) => prevQuestions.filter(({ id }) => id !== idToDelete));
      setValue('interview_questions', updatedInterviewQuestions);
      return;
    }

    // If question exists in DB, show modal (with or without media)
    setQuestionToDelete(idToDelete);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteQuestion = async () => {
    if (!questionToDelete) return;

    setIsDeletingQuestion(true);

    // Store current state for potential rollback
    const originalQuestions = questions;
    const originalFormValues = getValues();

    try {
      // Prepare the updated questions first
      const currentFormValuesDelete = getValues();
      const updatedInterviewQuestions =
        currentFormValuesDelete.interview_questions?.filter(
          (q: { id: string }) => q.id !== questionToDelete,
        ) || [];

      // Check if any remaining questions are empty and fill them with database values
      const questionsWithRestoredValues = updatedInterviewQuestions.map((question) => {
        // If the question is empty, restore it from the database
        if (question.question.trim() === '') {
          const originalQuestion = singleProjectData?.interview_questions?.find(
            ({ id }) => id === question.id,
          );
          if (originalQuestion) {
            return { ...question, question: originalQuestion.question };
          }
        }
        return question;
      });

      // Submit to server first
      await onSubmit({ interview_questions: questionsWithRestoredValues }, true);

      // Synchronize questions state with the successful form update
      setQuestions(
        questionsWithRestoredValues.map((formQuestion) => {
          // Find matching question from current state to preserve any local modifications
          const currentQuestion = questions.find((q) => q.id === formQuestion.id);
          return currentQuestion || formQuestion;
        }),
      );

      // Update form values in sync with questions state
      setValue('interview_questions', questionsWithRestoredValues);
      toast.success(lang.get('msg.questionDeleted'));
      setIsDeleteModalOpen(false);
      setQuestionToDelete(null);
    } catch (error) {
      setQuestions(originalQuestions);
      setValue('interview_questions', originalFormValues.interview_questions);

      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setIsDeletingQuestion(false);
    }
  };

  useEffect(() => {
    const initialInterviewQuestions: QuestionBoxProps[] =
      singleProjectData?.interview_questions || [];
    setQuestions(initialInterviewQuestions);
  }, [singleProjectData]);

  const isDisabled = isUserViewer || isProjectPublished || isProjectCompleted;
  const isAddingQuestionsDisabled =
    isDisabled || questions?.length >= QUESTIONS_LIMIT || uploadingQuestionId;
  const hasQuestions = questions?.length > 0;

  return (
    <>
      <div className="w-full h-full pl-1">
        <div className="flex flex-col w-full gap-6 pb-10">
          {/* -- introduction -- */}
          <RequiredLabel title="openingMessage" tooltip={lang.get('msg.openingMessageInfo')}>
            <Textarea
              disabled={isUserViewer || isProjectPublished || isProjectCompleted}
              {...register('opening_message')}
              placeholder={lang.get('msg.openingMessagePlaceholder')}
              onChange={(e) => {
                setIsFormChanged(true);
                setValue('opening_message', e.target.value, { shouldDirty: true });
              }}
              className="h-24"
            />
          </RequiredLabel>

          {/* -- drag & drop area -- */}
          <div className="flex flex-col w-full h-full gap-4">
            <p className="flex items-center gap-1 text-[15px] font-medium">
              {lang.get('msg.interviewQuestions')}
            </p>
            {/* wrapper */}
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              onDragStart={() => setIsActiveDrag(true)}
            >
              <div className="flex flex-col w-full h-full gap-5 p-6 rounded-md bg-slate-50">
                {!hasQuestions && (
                  <p className="text-sm font-medium">{lang.get('msg.questionsFieldEmpty')}</p>
                )}
                <SortableContext
                  disabled={isDisabled}
                  items={questions}
                  strategy={verticalListSortingStrategy}
                >
                  {questions.map((question, index) => (
                    <QuestionBox
                      isProjectPublished={isProjectPublished}
                      isProjectCompleted={isProjectCompleted}
                      isActiveDrag={isActiveDrag}
                      key={question.id}
                      index={index}
                      register={register}
                      id={question.id}
                      control={control}
                      handleDeleteQuestion={handleDeleteQuestion}
                      isUserViewer={isUserViewer}
                      existingText={question.question}
                      singleProjectData={singleProjectData}
                      questions={questions}
                      openQuestionId={openQuestionId}
                      setOpenQuestionId={setOpenQuestionId}
                      setFileData={setFileData}
                      fileData={fileData}
                      currentQuestion={currentQuestions?.[index]}
                      setValue={setValue}
                      clearErrors={clearErrors}
                      error={errors?.interview_questions?.[index]?.question}
                    />
                  ))}
                </SortableContext>
              </div>
            </DndContext>
            <div className="flex items-center justify-between w-full gap-2">
              {/* add question button */}
              <Button
                variant="default"
                disabled={isAddingQuestionsDisabled}
                type="button"
                onClick={addQuestion}
                className="flex items-center w-full gap-1"
              >
                {questions?.length < QUESTIONS_LIMIT && <IconPlus size={16} />}
                {questions?.length >= QUESTIONS_LIMIT
                  ? lang.get('msg.yourCurrentPlanOnlySupports40QuestionsPerProject')
                  : lang.get('msg.addQuestion')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <DeleteQuestionModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setQuestionToDelete(null);
        }}
        onConfirm={confirmDeleteQuestion}
        hasMedia={
          questionToDelete ? !!questions?.find(({ id }) => id === questionToDelete)?.media : false
        }
        isDeletingQuestion={isDeletingQuestion}
      />
    </>
  );
}

export default DiscussionGuide;
